import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Camera,
  UploadSimple,
  Sparkle,
  FloppyDisk,
  Tag,
  CurrencyGbp,
  Storefront,
  Plus,
  X,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  scanItem,
  createItem,
  fileToBase64,
  uploadFile,
  formatPrice,
  fileUrl,
} from "../lib/api";

const Field = ({ label, value, onChange, multiline, testid, type = "text", min }) => (
  <div>
    <label className="nb-label">{label}</label>
    {multiline ? (
      <textarea
        data-testid={testid}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        rows={6}
        className="nb-input mt-2 resize-y"
      />
    ) : (
      <input
        data-testid={testid}
        type={type}
        min={min}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className="nb-input mt-2"
      />
    )}
  </div>
);

export default function Scanner() {
  const fileRef = useRef(null);
  const cameraRef = useRef(null);
  const navigate = useNavigate();
  const [images, setImages] = useState([]); // {id, url, preview}
  const [scanning, setScanning] = useState(false);
  const [uploadingCount, setUploadingCount] = useState(0);
  const [result, setResult] = useState(null);
  const [saving, setSaving] = useState(false);
  const [listingPrice, setListingPrice] = useState("");
  const [costPrice, setCostPrice] = useState("");
  const [quantity, setQuantity] = useState(1);

  const acceptFiles = async (files) => {
    if (!files || files.length === 0) return;
    for (const f of files) {
      if (!/^image\/(jpeg|jpg|png|webp)$/i.test(f.type)) {
        toast.error("Use JPG, PNG, or WEBP only");
        continue;
      }
      const preview = await fileToBase64(f);
      setUploadingCount((c) => c + 1);
      try {
        const up = await uploadFile(f);
        setImages((arr) => [...arr, { id: up.id, url: fileUrl(up.id), preview }]);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Upload failed");
      } finally {
        setUploadingCount((c) => c - 1);
      }
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    acceptFiles(Array.from(e.dataTransfer.files || []));
  };

  const removeImage = (idx) =>
    setImages((arr) => arr.filter((_, i) => i !== idx));

  const runScan = async () => {
    if (images.length === 0) return toast.error("Upload a photo first");
    setScanning(true);
    try {
      const r = await scanItem(images[0].preview);
      setResult(r);
      setListingPrice(String(r.estimated_value_max || ""));
      toast.success("Got it! Listing ready below.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Scan failed. Try a clearer photo.");
    } finally {
      setScanning(false);
    }
  };

  const updateField = (k, v) => setResult((p) => ({ ...p, [k]: v }));

  const save = async (status = "draft") => {
    if (!result) return;
    if (status === "listed" && !listingPrice) {
      return toast.error("Set a listing price before going live");
    }
    setSaving(true);
    try {
      const payload = {
        ...result,
        images: images.map((i) => i.id),
        listing_price: listingPrice ? Number(listingPrice) : null,
        cost_price: costPrice ? Number(costPrice) : 0,
        quantity: Math.max(1, Number(quantity) || 1),
        status,
      };
      const item = await createItem(payload);
      toast.success(status === "listed" ? "Listed on the marketplace!" : "Saved to drafts.");
      navigate(`/items/${item.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save item");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mx-auto max-w-7xl px-6 py-10 sm:px-10">
      <div>
        <span className="nb-label">Scan</span>
        <h1 className="mt-2 font-display text-4xl font-black leading-none tracking-tighter sm:text-5xl">
          One photo. <br />A full marketplace listing.
        </h1>
        <p className="mt-3 max-w-2xl text-base font-medium text-[#4A4A4A]">
          Drop multiple photos, set quantity. AI identifies the item, estimates value, and drafts a Vinted listing.
        </p>
      </div>

      <div className="mt-10 grid grid-cols-1 gap-8 lg:grid-cols-2">
        <div className="space-y-6">
          <div
            data-testid="upload-zone"
            onDrop={onDrop}
            onDragOver={(e) => e.preventDefault()}
            className="nb-card border-dashed bg-[#FDFBF7]"
            style={{ borderStyle: "dashed" }}
          >
            <input
              ref={fileRef}
              data-testid="file-input"
              type="file"
              accept="image/jpeg,image/png,image/webp"
              multiple
              className="hidden"
              onChange={(e) => acceptFiles(Array.from(e.target.files || []))}
            />
            <input
              ref={cameraRef}
              data-testid="camera-input"
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={(e) => acceptFiles(Array.from(e.target.files || []))}
            />
            {images.length > 0 ? (
              <div>
                <div className="grid grid-cols-3 gap-3">
                  {images.map((img, idx) => (
                    <div
                      key={img.id}
                      className="relative aspect-square overflow-hidden rounded-lg border-[3px] border-[#0A0A0A]"
                    >
                      <img src={img.preview} alt="" className="h-full w-full object-cover" />
                      <button
                        data-testid={`remove-image-${idx}`}
                        onClick={() => removeImage(idx)}
                        className="absolute right-1 top-1 flex h-7 w-7 items-center justify-center rounded-full border-[2px] border-[#0A0A0A] bg-[#FFA0A0]"
                      >
                        <X size={14} weight="bold" />
                      </button>
                      {idx === 0 && (
                        <span className="absolute left-1 top-1 nb-badge bg-[#FFDF85]">main</span>
                      )}
                    </div>
                  ))}
                  <button
                    data-testid="add-more"
                    onClick={() => fileRef.current?.click()}
                    className="flex aspect-square items-center justify-center rounded-lg border-[3px] border-dashed border-[#0A0A0A] bg-white hover:bg-[#FDFBF7]"
                  >
                    <Plus size={28} weight="bold" />
                  </button>
                </div>
                {uploadingCount > 0 && (
                  <div className="mt-3 text-sm font-bold uppercase tracking-wider text-[#4A4A4A]">
                    Uploading {uploadingCount}…
                  </div>
                )}
                <div className="mt-5 flex flex-wrap gap-3">
                  <button
                    data-testid="open-camera"
                    onClick={() => cameraRef.current?.click()}
                    className="nb-btn nb-btn-ghost inline-flex items-center gap-2"
                  >
                    <Camera weight="bold" size={18} /> Take photo
                  </button>
                  <button
                    data-testid="run-scan"
                    onClick={runScan}
                    disabled={scanning || uploadingCount > 0}
                    className="nb-btn inline-flex items-center gap-2 disabled:opacity-60"
                  >
                    {scanning ? (
                      <>
                        <Sparkle weight="duotone" size={18} className="animate-spin" /> Analyzing…
                      </>
                    ) : (
                      <>
                        <Sparkle weight="bold" size={18} /> Identify & price
                      </>
                    )}
                  </button>
                </div>
              </div>
            ) : (
              <div
                onClick={() => fileRef.current?.click()}
                className="flex cursor-pointer flex-col items-center py-10 text-center"
              >
                <div className="flex h-20 w-20 items-center justify-center rounded-2xl border-[3px] border-[#0A0A0A] bg-[#D4C4FB] shadow-[4px_4px_0px_0px_#0A0A0A]">
                  <UploadSimple weight="duotone" size={40} />
                </div>
                <h3 className="mt-5 font-display text-2xl font-black tracking-tight">Drop photos here</h3>
                <p className="mt-1 max-w-sm text-sm font-medium text-[#4A4A4A]">
                  JPG · PNG · WEBP. First photo becomes the listing cover.
                </p>
                <div className="mt-5 flex gap-3">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      fileRef.current?.click();
                    }}
                    className="nb-btn inline-flex items-center gap-2"
                  >
                    <UploadSimple weight="bold" size={18} /> Choose photos
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      cameraRef.current?.click();
                    }}
                    className="nb-btn nb-btn-secondary inline-flex items-center gap-2"
                  >
                    <Camera weight="bold" size={18} /> Take photo
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="space-y-6">
          {!result && !scanning && (
            <div className="nb-card bg-[#FFDF85]">
              <span className="nb-label">Waiting for a scan</span>
              <h3 className="mt-2 font-display text-2xl font-black tracking-tight">
                Your AI listing will appear here ✨
              </h3>
              <p className="mt-2 text-sm font-medium text-[#4A4A4A]">
                Tip: front of the garment, tags, good lighting.
              </p>
            </div>
          )}

          {scanning && (
            <div className="nb-card bg-[#D4C4FB]">
              <div className="flex items-center gap-3">
                <Sparkle weight="duotone" size={26} className="animate-spin" />
                <span className="font-display text-xl font-black">Identifying your item…</span>
              </div>
            </div>
          )}

          {result && (
            <>
              <div className="nb-card bg-[#B4F0A7]">
                <span className="nb-label">Identified</span>
                <h3 className="mt-1 font-display text-2xl font-black leading-tight">{result.product_name}</h3>
                <div className="mt-2 flex flex-wrap gap-2">
                  {result.brand && <span className="nb-badge bg-white">{result.brand}</span>}
                  {result.category && <span className="nb-badge bg-white">{result.category}</span>}
                  {result.condition && <span className="nb-badge bg-[#FFDF85]">{result.condition}</span>}
                  {result.color && <span className="nb-badge bg-white">{result.color}</span>}
                </div>
              </div>

              <div className="nb-card bg-[#FFDF85]">
                <div className="flex items-center gap-2">
                  <CurrencyGbp weight="duotone" size={22} />
                  <span className="nb-label">Estimated resale value</span>
                </div>
                <div className="mt-2 font-display text-4xl font-black tracking-tighter">
                  {formatPrice(result.estimated_value_min)} – {formatPrice(result.estimated_value_max)}
                </div>
                <div className="mt-3 flex items-center gap-2">
                  <Storefront weight="duotone" size={18} />
                  <span className="font-display text-sm font-bold">
                    Best:{" "}
                    <span className="rounded-md border-[2px] border-[#0A0A0A] bg-white px-2 py-0.5">
                      {result.suggested_platform}
                    </span>
                  </span>
                </div>
              </div>

              <div className="nb-card bg-white">
                <span className="nb-label">Edit listing</span>
                <div className="mt-4 grid grid-cols-1 gap-4">
                  <Field label="Title" value={result.suggested_title} onChange={(v) => updateField("suggested_title", v)} testid="edit-title" />
                  <Field label="Description" value={result.suggested_description} onChange={(v) => updateField("suggested_description", v)} multiline testid="edit-description" />
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="nb-label">Listing price (£)</label>
                      <input data-testid="edit-listing-price" type="number" value={listingPrice} onChange={(e) => setListingPrice(e.target.value)} className="nb-input mt-2" />
                    </div>
                    <div>
                      <label className="nb-label">What you paid</label>
                      <input data-testid="edit-cost-price" type="number" value={costPrice} onChange={(e) => setCostPrice(e.target.value)} className="nb-input mt-2" />
                    </div>
                    <div>
                      <label className="nb-label">Quantity</label>
                      <input data-testid="edit-quantity" type="number" min={1} value={quantity} onChange={(e) => setQuantity(e.target.value)} className="nb-input mt-2" />
                    </div>
                  </div>
                </div>
                <div className="mt-6 flex flex-wrap gap-3">
                  <button data-testid="save-draft" onClick={() => save("draft")} disabled={saving} className="nb-btn nb-btn-ghost inline-flex items-center gap-2 disabled:opacity-60">
                    <FloppyDisk weight="bold" size={18} /> Save draft
                  </button>
                  <button data-testid="save-listed" onClick={() => save("listed")} disabled={saving} className="nb-btn nb-btn-secondary inline-flex items-center gap-2 disabled:opacity-60">
                    <Storefront weight="bold" size={18} /> List on marketplace
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
